
import ActionButton from "Components/atoms/buttons/action-button"
import BudgetActualsTable from "Components/molecule/budget-actual-table"
import BudgetActualsLineChart from "Components/molecule/charts/budget-actuals-line-chart"
import CheckboxInput from "Components/molecule/inputs/checkbox-input"
import { SelectOption } from "Components/molecule/inputs/select-input"
import SyncButton from "Components/molecule/sync-button"
import AreYouSureModal from "Components/organisms/are-you-sure-modal"
import Breadcrumb from "Components/organisms/breadcrumb"
import SearchBudgetActualsForm from "Components/organisms/forms/search-budget-actuals-form"
import LoadingState from "Components/organisms/loading-state"
import WorkInProgressState from "Components/organisms/work-in-progress"
import useManual from "hooks/useManual"
import useSyncProcess from "hooks/useSyncProcess"
import Branch from "models/branch"
import Department from "models/department"
import { SyncLogTypes } from "models/sync-log"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Container } from "reactstrap"
import { createSelector } from "reselect"
import { BudgetActualsSliceState } from "slices/finance/budget-actuals/reducer"
import { getBudgetActuals, getBudgetActualsSearchFields, syncBudgetActuals } from "slices/finance/budget-actuals/thunk"
import { SyncLogSliceState } from "slices/sync-log/reducer"
import { getMostRecentSyncLog } from "slices/thunk"
import FiscalCalendar, { DEFAULT_FISCAL_START } from "utils/fiscal-calendar"
import useFiscalYearOptions from "hooks/useFiscalYearOptions"
import { boolean } from "yup"
import { HelpIcon } from "Components/atoms/icons"
import ReportButtons from "Components/molecule/report-buttons"
import { toDepartmentOptions } from "utils/department-options"


const DEFAULT_YEAR = FiscalCalendar.lastMonthFiscalYear().toString()
const VANCOUVER_BRANCH = "VANCOUVER"
const CALGARY_BRANCH = "CALGARY"

const selectBudgetActuals = createSelector(
    (state: any) => state.BudgetActual,
    ({ budgetActuals, branches, departments }: BudgetActualsSliceState) => ({
        budgetActuals, branches, departments
    })
)

const selectSyncLog = createSelector(
    (state: any) => state.SyncLog,
    ({ syncLogs }: SyncLogSliceState) => ({
        syncLogs
    })
)

type DepartmentDisplay = {
    departmentId: string
    label: string
    data: number[][]
}

type DivisionDisplay = {
    divId: string
    label: string
    sum: number[][]
    departments: DepartmentDisplay[]
}

type BranchDisplay = {
    branchId: string
    label: string
    division: DivisionDisplay[]
}


const BudgetActualsPage = () => {
    document.title = "FY WIH / Actuals / Funnel | IbexIQ";
    const { handleOpenManual } = useManual("finance/budget-actual");
    const dispatch = useDispatch() as any
    const [year, setYear] = useState<string>(DEFAULT_YEAR)
    const { options: fiscalYearOptions, defaultYear, isLoading: isLoadingFiscalYears } = useFiscalYearOptions()
    const [selectedBranches, setSelectedBranches] = useState<SelectOption[]>([])
    const [selectedDepartments, setSelectedDepartments] = useState<SelectOption[]>([])
    const [availableBranches, setAvailableBranches] = useState<Branch[]>([])
    const [availableDepartments, setAvailableDepartments] = useState<Department[]>([])
    const [showSyncModal, setShowSyncModal] = useState<boolean>(false)
    const [showDetail, setShowDetail] = useState<boolean>(true);
    const [isExportingData, setIsExportingDate] = useState<boolean>(false);
    const [loading, setLoading] = useState(true)
    const [sum, setSum] = useState<number[][]>([])
    const calendar = new FiscalCalendar(DEFAULT_FISCAL_START);


    const {
        budgetActuals,
        branches,
        departments,
    } = useSelector(selectBudgetActuals)

    const {
        syncLogs
    } = useSelector(selectSyncLog)

    const [currYear, currPeriod] = useMemo(() => {
        const { fiscalYear, period } = calendar.firstWorkInHandPeriod();
        return [fiscalYear, period]
    }, [calendar])

    useEffect(() => {
        const init = async () => {
            const resP: any[] = [];

            if (!branches.length || !departments.length)
                resP.push(dispatch(getBudgetActualsSearchFields()))


            if (!syncLogs[SyncLogTypes.REVENUE_IBEX])
                resP.push(dispatch(getMostRecentSyncLog(SyncLogTypes.REVENUE_IBEX)))

            resP.push(dispatch(getBudgetActuals(year)))
            await Promise.all(resP)
            setLoading(false)
        }

        init()
    }, [])


    useEffect(() => {
        if (!budgetActuals) return

        const branchIds = new Set<string>()
        const departmentIds = new Set<string>()

        Object.entries(budgetActuals).forEach(([branchId, departmentMap]) => {
            branchIds.add(branchId)
            Object.keys(departmentMap).forEach(departmentId => departmentIds.add(departmentId))
        })

        const availableBranches = branches.filter(b => branchIds.has(b.id))
        const availableDepartments = departments.filter(d => departmentIds.has(d.id))
        setAvailableBranches(availableBranches)
        setAvailableDepartments(availableDepartments)

        setSelectedBranches(availableBranches.filter(b => {
            const code = b.code.trim();
            return code === VANCOUVER_BRANCH || code === CALGARY_BRANCH
        }).map(b => ({ label: b.name, value: b.id })))
        setSelectedDepartments(availableDepartments.map(d => ({ label: d.description, value: d.id })))
    }, [budgetActuals, branches, departments])

    const changeYear = async (year: string) => {
        setYear(year);
        setLoading(true);
        await dispatch(getBudgetActuals(year));
        setLoading(false)
    }

    // If the current fiscal year is hidden, move to the newest visible year (only if the user hasn't picked one).
    useEffect(() => {
        if (!isLoadingFiscalYears && String(defaultYear) !== DEFAULT_YEAR && year === DEFAULT_YEAR) {
            changeYear(String(defaultYear))
        }
    }, [isLoadingFiscalYears, defaultYear])


    const calculateSum = useCallback(() => {
        if (!selectedBranches.length || !selectedDepartments.length) {
            setSum([])
            return
        }

        const total: number[][] = [[], [], [], [], [], [], [], []]
        for (const b of selectedBranches) {
            const branchData = budgetActuals[b.value]
            if (!branchData) continue

            for (const s of selectedDepartments) {
                const data = branchData[s.value]
                if (!data) continue


                const [actuals, budgets, planned, forecast, funnel, consultingIncome, accrued] = data
                const periods = Math.max(actuals.length, budgets.length)

                for (let p = 0; p < periods; p++) {
                    const actual = Number(actuals[p] ?? 0)
                    const budget = Number(budgets[p] ?? 0)
                    const plan = Number(planned[p] ?? 0)
                    const fr = Number(forecast[p] ?? 0)
                    const fu = Number(funnel[p] ?? 0)
                    const ci = Number(consultingIncome[p] ?? 0)
                    const ac = Number(accrued[p] ?? 0)

                    total[0][p] = (total[0][p] ?? 0) + actual
                    total[1][p] = (total[1][p] ?? 0) + budget

                    // If current or future periods then diff between planned and budget
                    if (parseFloat(year) + ((p + 1) / 100) >= (currYear + (currPeriod / 100)))
                        total[2][p] = (total[2][p] ?? 0) + (fr - budget)
                    else
                        total[2][p] = (total[2][p] ?? 0) + (actual - budget)

                    total[3][p] = (total[3][p] ?? 0) + plan
                    total[4][p] = (total[4][p] ?? 0) + fr
                    total[5][p] = (total[5][p] ?? 0) + fu
                    total[6][p] = (total[6][p] ?? 0) + ci
                    total[7][p] = (total[7][p] ?? 0) + ac

                }
            }
        }

        setSum(total)
    }, [budgetActuals, selectedBranches, selectedDepartments, currPeriod, currYear, year])

    useEffect(() => {
        calculateSum()
    }, [selectedBranches, selectedDepartments, calculateSum])

    const departmentDisplay: BranchDisplay[] = useMemo(() => {
        if (!budgetActuals) return []

        return selectedBranches.map(branch => {
            const branchData = budgetActuals[branch.value]
            if (!branchData) return null

            const divisionMap = new Map<string, DivisionDisplay>()

            selectedDepartments.forEach(sub => {
                const departmentMeta = departments.find(d => d.id === sub.value)
                if (!departmentMeta) return
                const division = departmentMeta.parentDepartment
                if (!division) return

                const data = branchData[sub.value]
                if (!data) return

                const [actuals, budgets, planned, forecast, funnel, consultingIncome, accrued] = data
                const periods = Math.max(actuals.length, budgets.length)

                // [actuals], [budget], [diff], [planned], [forecast], [funnel], [consultingIncome], [accrued]
                const table: number[][] = [[], [], [], [], [], [], [], []]

                for (let p = 0; p < periods; p++) {
                    const a = Number(actuals[p] ?? 0)
                    const b = Number(budgets[p] ?? 0)
                    const pl = Number(planned[p] ?? 0)
                    const fr = Number(forecast[p] ?? 0)
                    const fu = Number(funnel[p] ?? 0)
                    const ci = Number(consultingIncome[p] ?? 0)
                    const ac = Number(accrued[p] ?? 0)

                    table[0][p] = a
                    table[1][p] = b

                    //
                    if (parseFloat(year) + ((p + 1) / 100) >= (currYear + (currPeriod / 100)))
                        table[2][p] = (table[2][p] ?? 0) + (fr - b)

                    else
                        table[2][p] = (table[2][p] ?? 0) + (a - b)

                    table[3][p] = pl
                    table[4][p] = fr
                    table[5][p] = fu
                    table[6][p] = ci
                    table[7][p] = ac
                }

                if (!divisionMap.has(division.id)) {
                    divisionMap.set(division.id, {
                        divId: division.id,
                        label: division.description,
                        sum: [],
                        departments: [],
                    })
                }

                divisionMap.get(division.id)!.departments.push({
                    departmentId: sub.value,
                    label: departmentMeta.description,
                    data: table,
                })
            })

            const divArray = Array.from(divisionMap.values())

            const summedDivArray = divArray.map(dA => {
                // [actuals], [budget], [diff], [planned], [forecast], [funnel], [consultingIncome], [accrued]
                const res: number[][] = [[], [], [], [], [], [], [], []];

                const deptBudgetActual = dA.departments;
                for (let i = 0; i < deptBudgetActual.length; i++) {
                    const data = deptBudgetActual[i].data

                    for (let j = 0; j < data[0].length; j++) {

                        res[0][j] = (res[0][j] ?? 0) + data[0][j] // Actual
                        res[1][j] = (res[1][j] ?? 0) + data[1][j] // Budget
                        res[2][j] = (res[2][j] ?? 0) + data[2][j] // Diff
                        res[3][j] = (res[3][j] ?? 0) + data[3][j] // Planned
                        res[4][j] = (res[4][j] ?? 0) + data[4][j] // Forecast
                        res[5][j] = (res[5][j] ?? 0) + data[5][j] // Funnel
                        res[6][j] = (res[6][j] ?? 0) + data[6][j] // Consulting Income
                        res[7][j] = (res[7][j] ?? 0) + data[7][j] // Accrued
                    }
                }

                return {
                    ...dA,
                    sum: res
                }
            })

            return {
                branchId: branch.value,
                label: branch.label,
                division: summedDivArray
            }
        }).filter(boolean) as unknown as BranchDisplay[]
    }, [
        budgetActuals,
        selectedBranches,
        selectedDepartments,
        departments,
        year,
        currYear,
        currPeriod,
    ])

    const handleOpenAreYouSureModal = () => {
        setShowSyncModal(true);
    }

    const handleCloseAreYouSureModal = () => {
        setShowSyncModal(false)
    }

    const { isSyncing: isSyncingData, startSync } = useSyncProcess({
        module: SyncLogTypes.REVENUE_IBEX,
        start: () => syncBudgetActuals(year),
        onFinished: () => changeYear(year),
    })

    const syncFinancialYear = async (_year: string) => {
        handleCloseAreYouSureModal()
        await startSync()
    }


    const getMonthHeaders = (periodCount: number): string[] => {
        return Array.from({ length: periodCount }, (_, i) =>
            calendar.periodToMonth(i + 1)
        );
    };


    const handleExportData = () => {
        setIsExportingDate(true);

        try {
            const rows: string[][] = [];

            const pushBlock = (
                section: string,
                branch: string,
                division: string,
                sub: string,
                data: number[][]
            ) => {
                const [actuals, budgets, diff, , forecast, funnel, consultingIncome, accrued] = data;
                const periodCount = Math.max(actuals?.length ?? 0, budgets?.length ?? 0);
                const monthHeaders = getMonthHeaders(periodCount);

                const exportRows: [string, number[]][] = [
                    ["Budget", budgets],
                    ["Actual", actuals],
                    ["WIH", forecast],
                    ["Diff", diff],
                    ["Funnel", funnel],
                    ["Accrued", accrued],
                    ["Consulting Income", consultingIncome],
                ];

                exportRows.forEach(([label, row]) => {
                    const total = (row ?? []).reduce((acc, v) => acc + (v ?? 0), 0);
                    rows.push([
                        section,
                        branch,
                        division,
                        sub,
                        label,
                        ...monthHeaders.map((_, idx) => (row?.[idx] ?? 0).toFixed(2)),
                        total.toFixed(2),
                    ]);
                });
            };

            const spacer = () => rows.push([]);

            const maxPeriods =
                sum[0]?.length ??
                departmentDisplay[0]?.division[0]?.departments[0]?.data[0]?.length ??
                12;

            // Header row
            rows.push([
                "Section",
                "Branch",
                "Division",
                "Department",
                "Type",
                ...getMonthHeaders(maxPeriods),
                "Total",
            ]);

            // RAM Total
            if (sum.length) {
                pushBlock("RAM", "", "", "TOTAL", sum);
                spacer();
                spacer();
            }

            // Per branch > division > department
            departmentDisplay.forEach(branch => {
                branch.division.forEach(div => {
                    // Division summary row
                    pushBlock("Branch", branch.label, div.label, "TOTAL", div.sum);
                    spacer();

                    // Department rows
                    div.departments.forEach(sub => {
                        pushBlock("Branch", branch.label, div.label, sub.label, sub.data);
                        spacer();
                    });

                    spacer();
                });
            });

            // Build CSV
            const csv = rows
                .map(r =>
                    r.map(v =>
                        `"${String(v).replace(/"/g, '""')}"`
                    ).join(",")
                )
                .join("\n");

            // Download
            const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);

            const a = document.createElement("a");
            a.href = url;
            a.download = `budget-actuals-${year}.csv`;
            a.click();

            URL.revokeObjectURL(url);
        } finally {
            setIsExportingDate(false);
        }
    };

    return (
        <div className="page-content">
            <Container fluid style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                <Breadcrumb
                    title="Finance"
                    breadcrumbItem="FY WIH / Actuals / Funnel"
                    actions={
                        <>
                            <SyncButton
                                id="budgetActualsSyncBtn"
                                isSyncing={isSyncingData}
                                syncLog={syncLogs[SyncLogTypes.REVENUE_IBEX]}
                                onClick={handleOpenAreYouSureModal}
                            />
                            {
                                <ActionButton
                                    onClick={handleOpenManual}
                                >
                                    Help <HelpIcon />
                                </ActionButton>
                            }

                        </>
                    }
                />
                <div className="section-container">

                    <SearchBudgetActualsForm
                        branches={availableBranches.map(b => ({ label: b.name, value: b.id }))}
                        departments={toDepartmentOptions(availableDepartments)}
                        financialYears={fiscalYearOptions}
                        selectedBranches={selectedBranches}
                        selectedDepartments={selectedDepartments}
                        selectedYear={year}
                        onChangeBranches={setSelectedBranches}
                        onChangeDepartments={setSelectedDepartments}
                        onChangeYear={v => v && changeYear(v?.value)}
                        onClear={() => {
                            setSelectedBranches([])
                            setSelectedDepartments([])
                            setYear(String(defaultYear))
                        }}
                        showDetail={showDetail}
                    />
                </div>

                {loading ? (
                    <LoadingState title="Loading" subtitle="Crunching numbers" />
                ) : !selectedBranches.length || !selectedDepartments.length ? (
                    <WorkInProgressState
                        title="Missing Requirements"
                        subtitle="Please select a branch and department"
                    />
                ) : (
                    <>
                        <div style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}>


                            <h3 style={{ margin: '0px' }}>RAM</h3>
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    gap: '20px'
                                }}
                            >
                                <CheckboxInput
                                    label={"Show Funnel"}
                                    checked={showDetail}
                                    onChange={(e) => setShowDetail(e.target.checked)}
                                    containerStyles={{
                                        display: 'flex',
                                        flexDirection: 'row-reverse',
                                        justifyContent: 'flex-start',
                                        alignItems: 'center',
                                        gap: '8px',
                                        minWidth: 'unset'
                                    }}
                                />
                                <ReportButtons title="FY WIH / Actuals / Funnel" module="Finance" fullExport={handleExportData} />
                            </div>
                        </div>
                        <div className="section-container">
                            <BudgetActualsTable
                                data={sum}
                                fiscalYear={parseFloat(year)}
                                nowFiscalYear={currYear}
                                nowPeriod={currPeriod}
                                showDetail={showDetail}
                            />
                        </div>
                        {/* <div className="section-container">
                            <BudgetActualsLineChart
                                data={sum}
                                fiscalYear={parseFloat(year)}
                                nowFiscalYear={currYear}
                                nowPeriod={currPeriod}
                            />
                        </div> */}
                        {departmentDisplay.map(branch => (
                            <>
                                <h3>{branch.label}</h3>

                                {branch.division.map(div => (
                                    <>
                                        <div key={branch.branchId} className="section-container">
                                            <h4>{div.label}</h4>
                                            <BudgetActualsTable
                                                key={div.divId}
                                                data={div.sum}
                                                fiscalYear={parseFloat(year)}
                                                nowFiscalYear={currYear}
                                                nowPeriod={currPeriod}
                                                showDetail={showDetail}

                                            />
                                            {
                                                div.departments && div.departments.map(sub => (
                                                    <BudgetActualsTable
                                                        key={sub.departmentId}
                                                        title={sub.label}
                                                        data={sub.data}
                                                        fiscalYear={parseFloat(year)}
                                                        nowFiscalYear={currYear}
                                                        nowPeriod={currPeriod}
                                                        showDetail={showDetail}
                                                    />
                                                ))
                                            }
                                        </div>
                                    </>
                                ))}
                            </>
                        ))}
                    </>
                )}

                <AreYouSureModal
                    isOpen={showSyncModal}
                    onModalClose={handleCloseAreYouSureModal}
                    handleAreYouSureDecline={async () => handleCloseAreYouSureModal()}
                    handleAreYouSureAccept={() => syncFinancialYear(year)}
                    modalTitle={"Sync Budget Actuals"}
                    subTitle={`Financial year ${year}`}
                    areYouSureacceptText={"Sync"}
                    areYouSuredeclineText={"Cancel"}
                />

                <AreYouSureModal
                    isOpen={showSyncModal}
                    onModalClose={handleCloseAreYouSureModal}
                    handleAreYouSureDecline={async () => handleCloseAreYouSureModal()}
                    handleAreYouSureAccept={() => syncFinancialYear(year)}
                    modalTitle={"Sync Budget Actuals"}
                    subTitle={`Financial year ${year}`}
                    areYouSureacceptText={"Sync"}
                    areYouSuredeclineText={"Cancel"}
                />
            </Container>
        </div>
    )
}

export default BudgetActualsPage
